import { ArrowRightIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import {
  Button,
  DateInput,
  NumberInput,
  RadioButton,
  RadioGroup,
  Select,
  TextInput,
  usePageTitle
} from 'slab'
import { usePostPayrollPaySchedules, usePutPayrollPaySchedulesId } from 'utils/web_api'
import {
  PostPayrollPaySchedulesBankClosureStrategy as BankClosureStrategy,
  PostPayrollPaySchedulesFrequency as PayFrequency,
} from 'utils/web_api.schemas'

import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { usePrefill } from '../PrefillProvider'
import { emitEmployerOnboardingEvent } from '../utils'
import { PayDatePreview } from './pay_schedule/PayDatePreview'
import { usePayrollConfig } from './PayrollConfigProvider'
import { usePaySchedule } from './PayScheduleProvider'

const PayFrequencyLabels = {
  [PayFrequency.monthly]: 'Monthly',
  [PayFrequency.semi_monthly]: 'Semi-Monthly',
  [PayFrequency.bi_weekly]: 'Bi-Weekly',
  [PayFrequency.weekly]: 'Weekly',
}

function looksLikeDefaultName (name: string) {
  return !name || /^(Monthly|Semi-Monthly|Bi-Weekly|Weekly) \((\d{4})\)$/.test(name)
}

function getDefaultName (frequency: PayFrequency) {
  if (frequency) {
    const currentYear = new Date().getFullYear()
    return `${PayFrequencyLabels[frequency]} (${currentYear})`
  }

  return ''
}

export function PaySchedulePage () {
  usePageTitle('Configure your pay schedule')

  const { employer } = useEmployer()
  const { paySchedule, refetch: refetchPaySchedule } = usePaySchedule()
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()
  const { getPrefillValue } = usePrefill()
  const { payrollConfig } = usePayrollConfig()

  // If the employer has a payroll connection that supports pay schedule sync,
  // we don't want them to create a manual pay schedule. Redirect them back.
  // But if they have a connection that doesn't support sync (like shared connections),
  // they still need to set up their pay schedule manually.
  useEffect(() => {
    if (employer?.has_payroll_connection && payrollConfig?.supports_pay_schedule_sync) {
      navigateToPreviousStep()
    }
  }, [employer, payrollConfig])

  const createPayScheduleMutation = usePostPayrollPaySchedules({
    mutation: {
      onSuccess: async () => {
        await refetchPaySchedule()
        navigateToNextStep()
      }
    }
  })
  const updatePayScheduleMutation = usePutPayrollPaySchedulesId({
    mutation: {
      onSuccess: async () => {
        await refetchPaySchedule()
        navigateToNextStep()
      }
    }
  })

  const form = useForm({
    defaultValues: {
      frequency: paySchedule?.frequency ?? getPrefillValue('payFrequency') as PayFrequency,
      name: paySchedule?.name ?? getPrefillValue('payScheduleName') ?? '',
      bankClosureStrategy: paySchedule?.bank_closure_strategy ?? getPrefillValue('bankClosureStrategy') ?? '' as BankClosureStrategy,
      firstDay: paySchedule?.first_day ?? getPrefillValue('firstPayDay') ?? undefined as number | undefined,
      secondDay: paySchedule?.second_day ?? getPrefillValue('secondPayDay') ?? undefined as number | undefined,
      referencePayDate: paySchedule?.reference_pay_date ?? getPrefillValue('referencePayDate') ?? undefined as string | undefined,
    },
  })

  function onSubmit () {
    const data = form.getValues()
    emitEmployerOnboardingEvent('employeronboarding_configure_deductions', {
      version: 'neo',
    })
    if (paySchedule) {
      updatePayScheduleMutation.mutate({
        id: paySchedule.id,
        data: {
          frequency: data.frequency,
          name: data.name,
          bank_closure_strategy: data.bankClosureStrategy,
          first_day: data.firstDay,
          second_day: data.secondDay,
          reference_pay_date: data.referencePayDate,
        },
      })
    } else {
      createPayScheduleMutation.mutate({
        data: {
          frequency: data.frequency,
          name: data.name,
          bank_closure_strategy: data.bankClosureStrategy,
          first_day: data.firstDay,
          second_day: data.secondDay,
          reference_pay_date: data.referencePayDate,
        }
      })
    }
  }

  const frequency = form.watch('frequency')
  const bankClosureStrategy = form.watch('bankClosureStrategy')
  const firstDay = form.watch('firstDay')
  const secondDay = form.watch('secondDay')
  const referencePayDate = form.watch('referencePayDate')

  useEffect(() => {
    if (looksLikeDefaultName(form.getValues('name'))) {
      form.setValue('name', getDefaultName(frequency))
    }
  }, [frequency])

  function renderPayDateFields () {
    if (frequency === PayFrequency.monthly) {
      return (
        <div className='grid grid-cols-2 gap-4'>
          <NumberInput
            {...form.register('firstDay', {
              required: 'This field is required',
              valueAsNumber: true,
            })}
            label='Monthly pay day'
            description="Enter a number between 1 and 31. For shorter months, we'll use the last day of the month."
            placeholder='ex. 1'
            error={form.formState.errors.firstDay?.message}
          />
        </div>
      )
    }

    if (frequency === PayFrequency.semi_monthly) {
      return (
        <div className='grid grid-cols-2 gap-4'>
          <NumberInput
            {...form.register('firstDay', {
              required: 'This field is required',
              valueAsNumber: true,
            })}
            label='First pay day'
            placeholder='ex. 1'
            error={form.formState.errors.firstDay?.message}
          />
          <NumberInput
            {...form.register('secondDay', {
              required: 'This field is required',
              valueAsNumber: true,
            })}
            label='Second pay day'
            placeholder='ex. 15'
            error={form.formState.errors.secondDay?.message}
          />
        </div>
      )
    }

    if (frequency === PayFrequency.bi_weekly || frequency === PayFrequency.weekly) {
      return (
        <div className='grid grid-cols-2 gap-4'>
          <DateInput
            {...form.register('referencePayDate', {
              required: 'This field is required',
              setValueAs: (value) => {
                if (!value) return value
                return new Date(value).toISOString().split('T')[0]
              },
            })}
            label='Example pay day'
            description='A previous or upcoming regular pay date not impacted by a holiday'
            error={form.formState.errors.referencePayDate?.message}
          />
        </div>
      )
    }
  }

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading='We need a bit more information about your payroll setup'
        subheading="Thatch will create payroll files you can upload to manage deductions. We'll use the information you provide here to ensure your employees' deductions are correctly distributed across pay periods."
      />
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <CenteredLayout.Container>
          <Select
            {...form.register('frequency', {
              required: 'This field is required',
            })}
            label='Pay frequency'
            placeholder='Select pay frequency'
          >
            {Object.values(PayFrequency).map((frequency) => (
              <option key={frequency} value={frequency}>
                {PayFrequencyLabels[frequency]}
              </option>
            ))}
          </Select>
          <TextInput
            {...form.register('name', { required: 'This field is required' })}
            label='Pay schedule name'
            placeholder='Name your pay schedule'
          />
          {renderPayDateFields()}
          <div className='flex flex-col sm:flex-row gap-6'>
            <RadioGroup
              label='On weekends and holidays, payments are made on:'
              name='bankClosureStrategy'
            >
              <RadioButton
                {...form.register('bankClosureStrategy', {
                  required: 'This field is required',
                })}
                value={BankClosureStrategy.before}
                label='Previous business day'
              />
              <RadioButton
                {...form.register('bankClosureStrategy', {
                  required: 'This field is required',
                })}
                value={BankClosureStrategy.after}
                label='Next business day'
              />
              <RadioButton
                {...form.register('bankClosureStrategy', {
                  required: 'This field is required',
                })}
                value={BankClosureStrategy.closest}
                label='Closest business day'
                description='On holidays that fall on a Sunday or Monday, employees are paid on the next business day. On Saturdays or other holidays, employees are paid on the previous business day.'
              />
            </RadioGroup>
            <PayDatePreview
              frequency={frequency}
              bankClosureStrategy={bankClosureStrategy}
              firstDay={firstDay}
              secondDay={secondDay}
              referencePayDate={referencePayDate}
            />
          </div>
        </CenteredLayout.Container>
        <CenteredLayout.Buttons>
          <Button variant='secondary' onClick={navigateToPreviousStep}>
            Back
          </Button>
          <Button
            variant='primary'
            disabled={
              !form.formState.isValid || createPayScheduleMutation.isPending
            }
            rightIcon={ArrowRightIcon}
            type='submit'
          >
            Continue
          </Button>
        </CenteredLayout.Buttons>
      </form>
    </CenteredLayout>
  )
}
